import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EventOccurrence } from '../../shared/contracts';
import { formatEventRange, toLocalInput } from '../format';
import './calendar-event-preview.css';

function startTime(event: EventOccurrence) {
  if (event.allDay) return 'All day';
  const local = toLocalInput(event.start, event.timezone);
  const hour = Number(local.slice(11, 13));
  return `${hour % 12 || 12}:${local.slice(14, 16)} ${hour >= 12 ? 'PM' : 'AM'}`;
}

export type CalendarPreviewState = {
  event: EventOccurrence;
  left: number;
  top: number;
};

type CalendarEventPreviewProps = {
  event: EventOccurrence;
  active: boolean;
  previewId: string;
  onHover: (event: EventOccurrence, root: HTMLElement) => void;
  onFocus: (event: EventOccurrence, root: HTMLElement) => void;
  onLeave: (occurrenceId: string) => void;
  onBlur: (occurrenceId: string) => void;
  onDismiss: () => void;
  onEdit?: (event: EventOccurrence) => void;
};

export function CalendarEventPreview({
  event,
  active,
  previewId,
  onHover,
  onFocus,
  onLeave,
  onBlur,
  onDismiss,
  onEdit,
}: CalendarEventPreviewProps) {
  const content = useRef<HTMLSpanElement>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const [editHost, setEditHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const button = editButton.current;
    if (!button || !onEdit) return;
    // Stop at the button before FullCalendar's native event handlers run.
    const stop = (e: Event) => {
      e.stopPropagation();
      if (e.type === 'mousedown') e.preventDefault();
    };
    const edit = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
      onEdit(event);
    };
    button.addEventListener('click', edit);
    button.addEventListener('pointerdown', stop);
    button.addEventListener('mousedown', stop);
    button.addEventListener('touchstart', stop);
    button.addEventListener('keydown', stop);
    return () => {
      button.removeEventListener('click', edit);
      button.removeEventListener('pointerdown', stop);
      button.removeEventListener('mousedown', stop);
      button.removeEventListener('touchstart', stop);
      button.removeEventListener('keydown', stop);
    };
  }, [editHost, event, onEdit, onDismiss]);
  useEffect(() => {
    const root = content.current?.closest<HTMLElement>('.fc-event');
    const host = root?.matches('tr')
      ? root.querySelector<HTMLElement>('.fc-list-event-title')
      : root?.parentElement;
    if (!root || !host || !onEdit) {
      setEditHost(null);
      return;
    }
    root.classList.add('calendar-event-editable');
    host.classList.add('calendar-event-edit-host');
    setEditHost(host);
    return () => {
      root.classList.remove('calendar-event-editable');
      host.classList.remove('calendar-event-edit-host');
    };
  }, [onEdit]);
  useEffect(() => {
    const root = content.current?.closest<HTMLElement>('.fc-event');
    if (!root) return;
    const hover = () => {
      if (matchMedia('(hover: hover)').matches) onHover(event, root);
    };
    const leave = () => onLeave(event.occurrenceId);
    const blur = () => onBlur(event.occurrenceId);
    const focus = () => onFocus(event, root);
    const keyDown = (key: KeyboardEvent) => {
      if (key.target !== root) return;
      if (key.key === 'Escape') onDismiss();
      if (key.key === 'Enter' || key.key === ' ') {
        key.preventDefault();
        onDismiss();
        root.click();
      }
    };
    root.tabIndex = 0;
    root.setAttribute('role', 'button');
    root.setAttribute(
      'aria-label',
      `${event.title}. ${event.allDay ? 'All day. ' : ''}${formatEventRange(event)}.${event.status === 'draft' ? ' Draft.' : event.status === 'cancelled' ? ' Cancelled.' : ''}`,
    );
    root.removeAttribute('title');
    root.addEventListener('mouseenter', hover);
    root.addEventListener('mouseleave', leave);
    root.addEventListener('focus', focus);
    root.addEventListener('blur', blur);
    root.addEventListener('keydown', keyDown);
    root.addEventListener('click', onDismiss);
    if (active) root.setAttribute('aria-describedby', previewId);
    else root.removeAttribute('aria-describedby');
    return () => {
      root.removeEventListener('mouseenter', hover);
      root.removeEventListener('mouseleave', leave);
      root.removeEventListener('focus', focus);
      root.removeEventListener('blur', blur);
      root.removeEventListener('keydown', keyDown);
      root.removeEventListener('click', onDismiss);
      root.removeAttribute('aria-describedby');
    };
  }, [active, event, onBlur, onDismiss, onFocus, onHover, onLeave, previewId]);
  return (
    <>
      <span className="calendar-event-content" ref={content}>
        <time className="calendar-event-time" dateTime={event.start}>
          {startTime(event)}
        </time>
        {event.status === 'draft' && <span className="calendar-draft-label">Draft</span>}
        <span className="calendar-event-title">{event.title}</span>
      </span>
      {editHost &&
        onEdit &&
        createPortal(
          <button
            type="button"
            className="calendar-event-edit"
            aria-label={`Edit ${event.title}`}
            title="Edit event"
            ref={editButton}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m16 3 5 5-12 12-6 1 1-6Z M14 5l5 5" />
            </svg>
          </button>,
          editHost,
        )}
    </>
  );
}

export function CalendarEventTooltip({
  preview,
  previewId,
  onEnter,
  onLeave,
}: {
  preview: CalendarPreviewState | undefined;
  previewId: string;
  onEnter: () => void;
  onLeave: () => void;
}) {
  if (!preview) return null;
  const { event } = preview;
  return createPortal(
    <div
      id={previewId}
      role="tooltip"
      className="calendar-event-preview"
      style={{ left: preview.left, top: preview.top }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <strong>{event.title}</strong>
      {event.status === 'draft' && <span className="calendar-draft-label">Draft</span>}
      {event.status === 'cancelled' && <span>Cancelled</span>}
      <time dateTime={event.start}>
        {event.allDay ? 'All day · ' : ''}
        {formatEventRange(event)}
      </time>
      <span>{event.calendarName}</span>
      {event.physicalLocation && (
        <span>
          {event.physicalLocation.name}
          {event.physicalLocation.address ? ` · ${event.physicalLocation.address}` : ''}
        </span>
      )}
      {event.virtualLocation && (
        <span>Online{event.virtualLocation.label ? ` · ${event.virtualLocation.label}` : ''}</span>
      )}
      {event.summary && <p>{event.summary}</p>}
    </div>,
    document.body,
  );
}
